/**
 * urlParams.js - URL Parameter Parser
 * Liest URL-Parameter aus und überschreibt Settings VOR dem ersten Render
 *
 * Beispiel URL:
 * https://site.com/?bg=000000&meshColor=3D2314&hairColorRoot=3D2314&autoPlay=true
 *
 * Farben OHNE # angeben (wird automatisch hinzugefügt)
 */
import { settings } from "./settings.js";

// Wird später gesetzt wenn Module geladen sind
let applyDynamicSettingsCallback = null;

/**
 * Setzt die Callback-Funktion für dynamische Settings
 * Wird von test.js aufgerufen wenn alle Module geladen sind
 */
export function setDynamicSettingsCallback(callback) {
  applyDynamicSettingsCallback = callback;
}

/**
 * Wendet dynamische URL-Settings an (nach Scene-Init)
 * Diese Settings benötigen bereits initialisierte Objekte
 */
export function applyDynamicUrlSettings() {
  if (applyDynamicSettingsCallback && Object.keys(urlSettings).length > 0) {
    applyDynamicSettingsCallback(urlSettings);
  }
}

// Parsed URL-Parameter speichern für späteren Zugriff
export let urlSettings = {};

/**
 * Parst einen Farbwert (fügt # hinzu wenn nötig)
 */
function parseColor(value) {
  if (!value) return null;
  // Wenn bereits mit # beginnt, direkt zurückgeben
  if (value.startsWith("#")) return value;
  // Ansonsten # hinzufügen
  return `#${value}`;
}

/**
 * Parst einen Boolean-Wert
 */
function parseBool(value) {
  if (value === "true" || value === "1") return true;
  if (value === "false" || value === "0") return false;
  return null;
}

/**
 * Parst einen Float-Wert
 */
function parseFloat_(value) {
  const num = parseFloat(value);
  return isNaN(num) ? null : num;
}

/**
 * Parst einen Integer-Wert
 */
function parseInt_(value) {
  const num = parseInt(value, 10);
  return isNaN(num) ? null : num;
}

/**
 * Liest alle URL-Parameter aus und gibt sie als Objekt zurück
 * Erstellt ein case-insensitive Mapping für Webflow dataset Kompatibilität
 */
function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const result = {};

  // Mapping von lowercase zu camelCase für alle bekannten Parameter
  const paramMapping = {
    'bg': 'bg',
    'rotationspeed': 'rotationSpeed',
    'rotation': 'rotation',
    'breathing': 'breathing',
    'breathingintensity': 'breathingIntensity',
    'vertices': 'vertices',
    'verticesintensity': 'verticesIntensity',
    'mouseintensity': 'mouseIntensity',
    'mousedeform': 'mouseDeform',
    'metalness': 'metalness',
    'roughness': 'roughness',
    'displacementscale': 'displacementScale',
    'donutsize': 'donutSize',
    'donutvisible': 'donutVisible',
    'donutcolor': 'donutColor',
    'hair': 'hair',
    'hairgravity': 'hairGravity',
    'haircount': 'hairCount',
    'hairlength': 'hairLength',
    'hairgravitystrength': 'hairGravityStrength',
    'hairbendstrength': 'hairBendStrength',
    'haircolorroot': 'hairColorRoot',
    'haircolortip': 'hairColorTip',
    // Donut-spezifische Haar-Parameter
    'donuthaircount': 'donutHairCount',
    'donuthairlength': 'donutHairLength',
    'donuthairgravitystrength': 'donutHairGravityStrength',
    'donuthairbendstrength': 'donutHairBendStrength',
    'donuthaircolorroot': 'donutHairColorRoot',
    'donuthaircolortip': 'donutHairColorTip',
    'dissolve': 'dissolve',
    'dissolveedgecolor': 'dissolveEdgeColor',
    'dissolveedgewidth': 'dissolveEdgeWidth',
    'dissolvenoisescale': 'dissolveNoiseScale',
    'dissolvepulsecolor': 'dissolvePulseColor',
    'dissolveglowcolor': 'dissolveGlowColor',
    'dissolvepulsespeed': 'dissolvePulseSpeed',
    'dissolvepulseintensity': 'dissolvePulseIntensity',
    'dissolveveindensity': 'dissolveVeinDensity',
    'particlemorph': 'particleMorph',
    'text': 'text',
    'showmesh': 'showMesh',
    'autoplay': 'autoPlay',
    'autointerval': 'autoInterval',
    'meshcolor': 'meshColor',
    'logoimage': 'logoImage',
    'imagefilter': 'imageFilter',
    'blendmode': 'blendMode',
    'imagegrayscale': 'imageGrayscale',
    'imagevisible': 'imageVisible',
    'imagesize': 'imageSize',
    // Word-Display Parameter
    'worddisplay': 'wordDisplay',
    'worddisplaytext': 'wordDisplayText',
    'worddisplayhair': 'wordDisplayHair',
    'worddisplayshowmesh': 'wordDisplayShowMesh',
    'worddisplaymeshcolor': 'wordDisplayMeshColor',
    'worddisplayhairperletter': 'wordDisplayHairPerLetter',
    'worddisplayhairlength': 'wordDisplayHairLength',
    'worddisplayhaircolorroot': 'wordDisplayHairColorRoot',
    'worddisplayhaircolortip': 'wordDisplayHairColorTip',
    'worddisplaybendstrength': 'wordDisplayBendStrength',
    'worddisplaygravitystrength': 'wordDisplayGravityStrength',
    'worddisplayletterscale': 'wordDisplayLetterScale',
    'worddisplayletterspacing': 'wordDisplayLetterSpacing',
    'worddisplaylineheight': 'wordDisplayLineHeight',
    // Partikel-Morph Scale
    'particlemorphletterscale': 'particleMorphLetterScale',
  };

  for (const [key, value] of params.entries()) {
    // Trim whitespace from value (fixes Webflow trailing spaces)
    const trimmedValue = value.trim();
    // Check if lowercase key has a mapping, otherwise use original key
    const normalizedKey = paramMapping[key.toLowerCase()] || key;
    result[normalizedKey] = trimmedValue;
  }

  return result;
}

/**
 * Wendet URL-Parameter auf Settings an
 * Muss VOR dem ersten Render aufgerufen werden!
 *
 * Gibt ein Objekt mit den geparsten Werten zurück für scene.js etc.
 */
export function applyUrlParams() {
  const params = getUrlParams();
  urlSettings = {};

  // --- Hintergrund ---
  if (params.bg !== undefined) {
    urlSettings.background = parseColor(params.bg);
  }

  // --- Rotation & Animation ---
  if (params.rotationSpeed !== undefined) {
    const val = parseFloat_(params.rotationSpeed);
    if (val !== null) settings.rotationSpeed = val;
  }

  if (params.rotation !== undefined) {
    const val = parseBool(params.rotation);
    if (val !== null) settings.rotation = val;
  }

  if (params.breathing !== undefined) {
    const val = parseBool(params.breathing);
    if (val !== null) settings.breathing = val;
  }

  if (params.breathingIntensity !== undefined) {
    const val = parseFloat_(params.breathingIntensity);
    if (val !== null) settings.breathingIntensity = val;
  }

  if (params.vertices !== undefined) {
    const val = parseBool(params.vertices);
    if (val !== null) settings.vertices = val;
  }

  if (params.verticesIntensity !== undefined) {
    const val = parseFloat_(params.verticesIntensity);
    if (val !== null) settings.verticesIntensity = val;
  }

  if (params.mouseIntensity !== undefined) {
    const val = parseBool(params.mouseIntensity);
    if (val !== null) settings.mouseIntensity = val;
  }

  if (params.mouseDeform !== undefined) {
    const val = parseBool(params.mouseDeform);
    if (val !== null) settings.mouseDeform = val;
  }

  // --- Material ---
  if (params.metalness !== undefined) {
    const val = parseFloat_(params.metalness);
    if (val !== null) settings.metalness = val;
  }

  if (params.roughness !== undefined) {
    const val = parseFloat_(params.roughness);
    if (val !== null) settings.roughness = val;
  }

  if (params.displacementScale !== undefined) {
    const val = parseFloat_(params.displacementScale);
    if (val !== null) settings.displacementScale = val;
  }

  // --- Donut ---
  if (params.donutSize !== undefined) {
    const val = parseFloat_(params.donutSize);
    if (val !== null) settings.size = val;
  }

  if (params.donutVisible !== undefined) {
    const val = parseBool(params.donutVisible);
    if (val !== null) settings.donutVisible = val;
  }

  // --- Haar-Effekte (Donut) ---
  if (params.hair !== undefined) {
    const val = parseBool(params.hair);
    if (val !== null) settings.hair = val;
  }

  if (params.hairGravity !== undefined) {
    const val = parseBool(params.hairGravity);
    if (val !== null) settings.hairGravity = val;
  }

  // --- Donut-spezifische Haar-Settings ---
  if (params.donutHairCount !== undefined) {
    const val = parseInt_(params.donutHairCount);
    if (val !== null) urlSettings.donutHairCount = val;
  }

  if (params.donutHairLength !== undefined) {
    const val = parseFloat_(params.donutHairLength);
    if (val !== null) urlSettings.donutHairLength = val;
  }

  if (params.donutHairGravityStrength !== undefined) {
    const val = parseFloat_(params.donutHairGravityStrength);
    if (val !== null) urlSettings.donutHairGravityStrength = val;
  }

  if (params.donutHairBendStrength !== undefined) {
    const val = parseFloat_(params.donutHairBendStrength);
    if (val !== null) urlSettings.donutHairBendStrength = val;
  }

  if (params.donutHairColorRoot !== undefined) {
    const color = parseColor(params.donutHairColorRoot);
    if (color) urlSettings.donutHairColorRoot = color;
  }

  if (params.donutHairColorTip !== undefined) {
    const color = parseColor(params.donutHairColorTip);
    if (color) urlSettings.donutHairColorTip = color;
  }

  // --- Dissolve ---
  if (params.dissolve !== undefined) {
    const val = parseBool(params.dissolve);
    if (val !== null) settings.dissolve = val;
  }

  // --- Word-Display ---
  if (params.wordDisplay !== undefined) {
    const val = parseBool(params.wordDisplay);
    if (val !== null) settings.wordDisplay = val;
  }

  if (params.wordDisplayText !== undefined) {
    settings.wordDisplayText = params.wordDisplayText.toUpperCase();
  }

  if (params.wordDisplayHair !== undefined) {
    const val = parseBool(params.wordDisplayHair);
    if (val !== null) settings.wordDisplayHair = val;
  }

  if (params.wordDisplayShowMesh !== undefined) {
    const val = parseBool(params.wordDisplayShowMesh);
    if (val !== null) settings.wordDisplayShowMesh = val;
  }

  if (params.wordDisplayMeshColor !== undefined) {
    const color = parseColor(params.wordDisplayMeshColor);
    if (color) settings.wordDisplayMeshColor = color;
  }

  if (params.wordDisplayHairPerLetter !== undefined) {
    const val = parseInt_(params.wordDisplayHairPerLetter);
    if (val !== null) settings.wordDisplayHairPerLetter = val;
  }

  if (params.wordDisplayHairLength !== undefined) {
    const val = parseFloat_(params.wordDisplayHairLength);
    if (val !== null) settings.wordDisplayHairLength = val;
  }

  if (params.wordDisplayHairColorRoot !== undefined) {
    const color = parseColor(params.wordDisplayHairColorRoot);
    if (color) settings.wordDisplayHairColorRoot = color;
  }

  if (params.wordDisplayHairColorTip !== undefined) {
    const color = parseColor(params.wordDisplayHairColorTip);
    if (color) settings.wordDisplayHairColorTip = color;
  }

  if (params.wordDisplayBendStrength !== undefined) {
    const val = parseFloat_(params.wordDisplayBendStrength);
    if (val !== null) settings.wordDisplayBendStrength = val;
  }

  if (params.wordDisplayGravityStrength !== undefined) {
    const val = parseFloat_(params.wordDisplayGravityStrength);
    if (val !== null) settings.wordDisplayGravityStrength = val;
  }

  if (params.wordDisplayLetterScale !== undefined) {
    const val = parseFloat_(params.wordDisplayLetterScale);
    if (val !== null) settings.wordDisplayLetterScale = val;
  }

  if (params.wordDisplayLetterSpacing !== undefined) {
    const val = parseFloat_(params.wordDisplayLetterSpacing);
    if (val !== null) settings.wordDisplayLetterSpacing = val;
  }

  if (params.wordDisplayLineHeight !== undefined) {
    const val = parseFloat_(params.wordDisplayLineHeight);
    if (val !== null) settings.wordDisplayLineHeight = val;
  }

  // --- Partikel-Morph ---
  if (params.particleMorph !== undefined) {
    const val = parseBool(params.particleMorph);
    if (val !== null) settings.particleMorph = val;
  }

  if (params.text !== undefined) {
    settings.particleMorphText = params.text.toUpperCase();
  }

  if (params.showMesh !== undefined) {
    const val = parseBool(params.showMesh);
    if (val !== null) settings.particleMorphShowMesh = val;
  }

  if (params.autoPlay !== undefined) {
    const val = parseBool(params.autoPlay);
    if (val !== null) settings.particleMorphAutoPlay = val;
  }

  if (params.autoInterval !== undefined) {
    const val = parseInt_(params.autoInterval);
    if (val !== null) settings.particleMorphAutoInterval = val;
  }

  // --- Mesh-Farbe (Partikel-Morph) ---
  if (params.meshColor !== undefined) {
    const color = parseColor(params.meshColor);
    if (color) settings.particleMorphMeshColor = color;
  }

  if (params.particleMorphLetterScale !== undefined) {
    const val = parseFloat_(params.particleMorphLetterScale);
    if (val !== null) settings.particleMorphLetterScale = val;
  }

  // --- Donut Farbe ---
  if (params.donutColor !== undefined) {
    urlSettings.donutColor = parseColor(params.donutColor);
  }

  // --- Gemeinsame Haar-Settings (für beide Modi) ---
  if (params.hairCount !== undefined) {
    const val = parseInt_(params.hairCount);
    if (val !== null) {
      settings.particleMorphHairCount = val;
      urlSettings.hairCount = val;
    }
  }

  if (params.hairLength !== undefined) {
    const val = parseFloat_(params.hairLength);
    if (val !== null) {
      settings.particleMorphHairLength = val;
      urlSettings.hairLength = val;
    }
  }

  if (params.hairGravityStrength !== undefined) {
    const val = parseFloat_(params.hairGravityStrength);
    if (val !== null) {
      settings.particleMorphHairGravityStrength = val;
      urlSettings.hairGravityStrength = val;
    }
  }

  if (params.hairBendStrength !== undefined) {
    const val = parseFloat_(params.hairBendStrength);
    if (val !== null) {
      settings.particleMorphHairBendStrength = val;
      urlSettings.hairBendStrength = val;
    }
  }

  if (params.hairColorRoot !== undefined) {
    const color = parseColor(params.hairColorRoot);
    if (color) {
      settings.particleMorphHairColorRoot = color;
      urlSettings.hairColorRoot = color;
    }
  }

  if (params.hairColorTip !== undefined) {
    const color = parseColor(params.hairColorTip);
    if (color) {
      settings.particleMorphHairColorTip = color;
      urlSettings.hairColorTip = color;
    }
  }

  // --- Dissolve-Effekte ---
  if (params.dissolveEdgeColor !== undefined) {
    urlSettings.dissolveEdgeColor = parseColor(params.dissolveEdgeColor);
  }

  if (params.dissolveEdgeWidth !== undefined) {
    const val = parseFloat_(params.dissolveEdgeWidth);
    if (val !== null) urlSettings.dissolveEdgeWidth = val;
  }

  if (params.dissolveNoiseScale !== undefined) {
    const val = parseFloat_(params.dissolveNoiseScale);
    if (val !== null) urlSettings.dissolveNoiseScale = val;
  }

  if (params.dissolvePulseColor !== undefined) {
    urlSettings.dissolvePulseColor = parseColor(params.dissolvePulseColor);
  }

  if (params.dissolveGlowColor !== undefined) {
    urlSettings.dissolveGlowColor = parseColor(params.dissolveGlowColor);
  }

  if (params.dissolvePulseSpeed !== undefined) {
    const val = parseFloat_(params.dissolvePulseSpeed);
    if (val !== null) urlSettings.dissolvePulseSpeed = val;
  }

  if (params.dissolvePulseIntensity !== undefined) {
    const val = parseFloat_(params.dissolvePulseIntensity);
    if (val !== null) urlSettings.dissolvePulseIntensity = val;
  }

  if (params.dissolveVeinDensity !== undefined) {
    const val = parseFloat_(params.dissolveVeinDensity);
    if (val !== null) urlSettings.dissolveVeinDensity = val;
  }

  // --- Logo/Bild Settings ---
  if (params.logoImage !== undefined) {
    urlSettings.logoImage = params.logoImage;
  }

  if (params.imageFilter !== undefined) {
    urlSettings.imageFilter = params.imageFilter;
  }

  if (params.blendMode !== undefined) {
    urlSettings.blendMode = params.blendMode;
  }

  if (params.imageGrayscale !== undefined) {
    const val = parseBool(params.imageGrayscale);
    if (val !== null) urlSettings.imageGrayscale = val;
  }

  if (params.imageVisible !== undefined) {
    const val = parseBool(params.imageVisible);
    if (val !== null) urlSettings.imageVisible = val;
  }

  if (params.imageSize !== undefined) {
    const val = parseFloat_(params.imageSize);
    if (val !== null) urlSettings.imageSize = val;
  }

  // Log für Debugging
  if (Object.keys(params).length > 0) {
    console.log("URL params applied:", params);
    console.log("Parsed settings:", urlSettings);
  }

  return urlSettings;
}
